import { TrackedMap } from 'tracked-built-ins'
import { isDestroyed, isDestroying, registerDestructor } from '@ember/destroyable'
import { waitForPromise } from '@ember/test-waiters'

/**
 * A domain-blind, per-key query store: a stale-while-revalidate cache with in-flight dedupe,
 * generation-guarded writes, soft/hard invalidation and a bounded (gcTime + LRU) entry map.
 *
 * It knows nothing about what it is caching. Everything domain-shaped — which keys exist, what a
 * key's dimensions mean, which request a dimension set maps to, how responses are projected, which
 * keys are worth pinning — is supplied by the caller through `QueryCacheConfig`. The callers today
 * are the availability engine (`app/availability/`) and the bundle availability
 * (`app/bundle-availability/`); the split exists so the mechanism can be read (and tested) without
 * the policy, not for speculative reuse.
 *
 * Deliberately NOT in here, because they are policy and belong to the caller: request priority,
 * key derivation, response merging, loading/verifying signals, and any notion of "scope".
 */

export type EntryStatus = 'loading' | 'success' | 'error'

/**
 * One verbatim response, with its own status and freshness stamp.
 *
 * `invalidated: 'hard'` condemns an entry: its data must stop being served everywhere until a
 * fresh response replaces it. Soft invalidation is not a field — it reuses `dataUpdatedAt = 0`
 * ("serve stale, revalidate on next read"), the same mechanism the visibility path uses.
 */
export type CacheEntry<TData> = {
  data: TData
  status: EntryStatus
  dataUpdatedAt: number
  invalidated?: 'hard'
}

/**
 * A key paired with the exact dimensions it was built from. Carrying the dimensions alongside the
 * key is what lets a deferred fetch send the request its key represents, rather than dimensions
 * re-read later that may have drifted and would write a mismatched response under this key.
 */
export type KeyRequest<TDims> = { key: string; dims: TDims }

type Backoff<TDims> = {
  count: number
  retryAt: number
  dims: TDims
  timer: ReturnType<typeof setTimeout>
}

export type InvalidateOptions<TData> = {
  mode?: 'soft' | 'hard'
  predicate?: (_key: string, _entry: CacheEntry<TData>) => boolean
}

export type QueryCacheConfig<TDims, TData> = {
  /** Runs the request a key's dimensions describe. The one place the domain's transport lives. */
  fetch: (_dims: TDims) => Promise<TData>
  /** "Is there anything to show in this payload?" — used to decide first-load vs background. */
  hasData: (_data: TData) => boolean
  /** The payload a never-loaded or failed entry carries until a real response lands. */
  emptyData: () => TData
  /** Skips the request entirely (no loading write, no error) when the dimensions can't be asked. */
  shouldFetch?: (_dims: TDims) => boolean
  /** Fires only when a settled fetch actually wrote — never for a superseded or torn-down one. */
  onSettled?: (_key: string, _dims: TDims, _error?: unknown) => void
  /**
   * Keys eviction must never drop, re-resolved on every sweep.
   *
   * CONTRACT: this must cover every key the caller passes to `ensure`/`ensureDeferred`. A key that
   * is ensured but not pinned can be evicted and then immediately re-fetched, and once the map is
   * at the cap that becomes an evict -> refetch -> evict thrash between the ensured keys. The
   * in-flight exemption does not save it: it only protects the fetch window, and a sweep far
   * enough over cap still reaches the entry once it settles.
   *
   * SIZING: keep the pinned set comfortably below `maxEntries`. The LRU pass loops while the map
   * is over cap and skips pinned keys, so once the pinned count REACHES the cap every unpinned
   * entry is dropped on every sweep — the cache degenerates to "current scope only" and anything
   * the user navigated away from is refetched instead of being retained until gcTime.
   */
  pinnedKeys?: () => Set<string>
  /** How long a fetched entry is served as-is before a read triggers a background refetch. */
  staleTime?: number
  /** How long an entry may go unread before the eviction sweep reclaims it as cold. */
  gcTime?: number
  /** Upper bound on resident entries; a burst past this evicts least-recently-read UNPINNED ones. */
  maxEntries?: number
  /** Mark every entry stale (without fetching) when the tab becomes visible again. */
  revalidateOnVisible?: boolean
  retryBaseDelay?: number
  retryMaxDelay?: number
}

const DEFAULT_STALE_TIME = 5 * 60 * 1000
const DEFAULT_GC_TIME = 10 * 60 * 1000
const DEFAULT_MAX_ENTRIES = 50
const DEFAULT_RETRY_BASE_DELAY = 1000
const DEFAULT_RETRY_MAX_DELAY = 30 * 1000
const RETRY_JITTER = 0.25

export class QueryCache<TDims, TData> {
  /**
   * Source of truth: one verbatim response per key. Tracked, so every derivation over it
   * recomputes when an entry is written, invalidated or evicted.
   */
  readonly entries = new TrackedMap<string, CacheEntry<TData>>()

  // Deliberately NOT tracked: these coordinate dedupe/current-fetch identity and eviction
  // bookkeeping without invalidating derived state — stamping a read timestamp during a
  // projection read must not re-dirty that same projection.
  private readonly inFlight = new Map<string, Promise<void>>()
  private readonly lastReadAt = new Map<string, number>()
  private readonly backoffs = new Map<string, Backoff<TDims>>()

  private readonly ctx: object
  private readonly config: QueryCacheConfig<TDims, TData>
  private readonly staleTime: number
  private readonly gcTime: number
  private readonly maxEntries: number
  private readonly retryBaseDelay: number
  private readonly retryMaxDelay: number

  private evictionScheduled = false

  constructor(ctx: object, config: QueryCacheConfig<TDims, TData>) {
    this.ctx = ctx
    this.config = config
    this.staleTime = config.staleTime ?? DEFAULT_STALE_TIME
    this.gcTime = config.gcTime ?? DEFAULT_GC_TIME
    this.maxEntries = config.maxEntries ?? DEFAULT_MAX_ENTRIES
    this.retryBaseDelay = config.retryBaseDelay ?? DEFAULT_RETRY_BASE_DELAY
    this.retryMaxDelay = config.retryMaxDelay ?? DEFAULT_RETRY_MAX_DELAY

    if (config.revalidateOnVisible) this.setupVisibilityRevalidation()

    registerDestructor(this.ctx, () => this.clearAllBackoffs())
  }

  // Callers read cross-owner state to build keys, which throws once the owner is torn down, and a
  // settled fetch may land after teardown. Every path that writes or schedules goes through this.
  private alive(): boolean {
    return !isDestroying(this.ctx) && !isDestroyed(this.ctx)
  }

  // -------------------------------------------------------------------------------------------
  // Reads (pure — none of these schedule a fetch)
  // -------------------------------------------------------------------------------------------

  /** Pure lookup. Does NOT schedule a revalidation and does NOT stamp read warmth. */
  read(key: string): CacheEntry<TData> | undefined {
    return this.entries.get(key)
  }

  /** Pure lookup of an entry's payload, only when the entry is still usable. */
  peek(key: string): TData | undefined {
    const entry = this.entries.get(key)
    return this.isUsable(entry) ? entry.data : undefined
  }

  /**
   * An entry contributes as long as it still carries something to show — so stale, revalidating
   * and errored entries keep serving their last-known data rather than blanking out. A
   * hard-invalidated entry is unusable everywhere until a fresh response replaces it.
   */
  isUsable(entry: CacheEntry<TData> | undefined): entry is CacheEntry<TData> {
    if (!entry || entry.invalidated === 'hard') return false
    return entry.status === 'success' || this.config.hasData(entry.data)
  }

  /** Whether a fetch for this key is currently in flight. */
  isFetching(key: string): boolean {
    return this.inFlight.has(key)
  }

  /** The in-flight promise for a key, if any — for callers that need to await a keyed set. */
  pending(key: string): Promise<void> | undefined {
    return this.inFlight.get(key)
  }

  /**
   * Stamps read warmth for the eviction policy. Separate from `read`/`peek` on purpose: a
   * capacity-shaped read must be able to fold an entry without keeping it artificially warm, while
   * a render-shaped read must. `lastReadAt` is untracked, so this never re-dirties a computation
   * it runs inside.
   */
  touch(keys: string[]): void {
    const now = Date.now()
    for (const key of keys) this.lastReadAt.set(key, now)
  }

  /** True for a missing entry, an explicitly zeroed one, or one older than `staleTime`. */
  isStale(key: string, now: number = Date.now()): boolean {
    const entry = this.entries.get(key)
    if (!entry) return true
    if (entry.dataUpdatedAt === 0) return true
    return now - entry.dataUpdatedAt > this.staleTime
  }

  // -------------------------------------------------------------------------------------------
  // Writes
  // -------------------------------------------------------------------------------------------

  // Mark loading while preserving prior data, so a background revalidation never blanks the UI. A
  // hard-invalidated entry stays condemned while refetching. Stamps a read baseline so a
  // just-created (still in-flight) entry has a fresh age and is never mistaken for cold.
  private markLoading(key: string, prior: CacheEntry<TData> | undefined): void {
    this.entries.set(key, {
      data: prior?.data ?? this.config.emptyData(),
      status: 'loading',
      dataUpdatedAt: prior?.dataUpdatedAt ?? 0,
      ...(prior?.invalidated && { invalidated: prior.invalidated })
    })
    this.lastReadAt.set(key, Date.now())
  }

  // Fresh data is authoritative, so a successful write clears any hard-invalidation flag. Stamps
  // the write time as the read baseline and schedules an eviction sweep — a write is where the map
  // grows, so it is one of the two points the sweep runs from (the other being `ensure`).
  private writeSuccess(key: string, data: TData): void {
    const now = Date.now()
    this.entries.set(key, { data, status: 'success', dataUpdatedAt: now })
    this.lastReadAt.set(key, now)
    this.clearBackoff(key)
    this.scheduleEviction()
  }

  // Keep prior data on failure so a failed revalidation degrades gracefully rather than emptying,
  // and preserve a hard-invalidation flag so a failed refetch never resurrects condemned data.
  private writeError(key: string, prior: CacheEntry<TData> | undefined): void {
    this.entries.set(key, {
      data: prior?.data ?? this.config.emptyData(),
      status: 'error',
      dataUpdatedAt: prior?.dataUpdatedAt ?? 0,
      ...(prior?.invalidated && { invalidated: prior.invalidated })
    })
  }

  isBackingOff(key: string, now: number = Date.now()): boolean {
    const backoff = this.backoffs.get(key)
    return !!backoff && now < backoff.retryAt
  }

  failureCount(key: string): number {
    return this.backoffs.get(key)?.count ?? 0
  }

  private clearBackoff(key: string): void {
    const backoff = this.backoffs.get(key)
    if (!backoff) return
    clearTimeout(backoff.timer)
    this.backoffs.delete(key)
  }

  private clearAllBackoffs(): void {
    for (const { timer } of this.backoffs.values()) clearTimeout(timer)
    this.backoffs.clear()
  }

  private recordFailure(key: string, dims: TDims): void {
    const count = (this.backoffs.get(key)?.count ?? 0) + 1
    this.clearBackoff(key)

    const capped = Math.min(this.retryBaseDelay * 2 ** (count - 1), this.retryMaxDelay)
    const delay = capped * (1 + Math.random() * RETRY_JITTER)
    const timer = setTimeout(() => this.runScheduledRetry(key), delay)

    this.backoffs.set(key, { count, dims, retryAt: Date.now() + delay, timer })
  }

  private runScheduledRetry(key: string): void {
    const backoff = this.backoffs.get(key)
    if (!backoff) return

    this.backoffs.set(key, { ...backoff, retryAt: 0 })

    if (!this.alive() || this.inFlight.has(key)) return

    const pinned = this.config.pinnedKeys?.()
    if (pinned && !pinned.has(key)) return

    void this.track(key, Promise.resolve().then(() => this.performFetch(key, backoff.dims)), false)
  }

  // -------------------------------------------------------------------------------------------
  // Fetching
  // -------------------------------------------------------------------------------------------

  // A settled fetch may only write if the ctx is still alive and no newer fetch has taken over.
  private mayWrite(key: string, mine: Promise<void> | undefined): boolean {
    return this.alive() && this.inFlight.get(key) === mine
  }

  private async performFetch(key: string, dims: TDims): Promise<void> {
    const prior = this.entries.get(key)

    // Nothing to ask yet — leave any prior data in place, don't mark loading, don't error.
    if (this.config.shouldFetch && !this.config.shouldFetch(dims)) {
      this.inFlight.delete(key)
      return
    }

    if (this.alive()) this.markLoading(key, prior)

    const mine = this.inFlight.get(key)

    try {
      const data = await this.config.fetch(dims)
      if (this.mayWrite(key, mine)) {
        this.writeSuccess(key, data)
        this.config.onSettled?.(key, dims)
      }
    } catch (error) {
      if (this.mayWrite(key, mine)) {
        this.config.onSettled?.(key, dims, error)
        this.writeError(key, prior)
        this.recordFailure(key, dims)
      }
    } finally {
      if (this.inFlight.get(key) === mine) this.inFlight.delete(key)
    }
  }

  // A background fetch must never surface an unhandled rejection (performFetch already records
  // failures onto the entry). Only a *first load* (a key with nothing to show) is wrapped in a test
  // waiter, registered synchronously here so `await settled()` blocks until initial data renders —
  // the fetch itself is deferred to a microtask, so without an up-front waiter settled() would
  // resolve before it even starts. Background revalidations of already-loaded keys stay invisible
  // to settled(); wrapping them would couple settled() to stale-while-revalidate and prevent it
  // from ever reaching quiescence.
  private track(key: string, run: Promise<void>, blocking: boolean): Promise<void> {
    const settled = run.catch(() => {})
    const promise = blocking ? waitForPromise(settled) : settled
    this.inFlight.set(key, promise)
    return promise
  }

  // A hard-invalidated entry counts as having no data, so its refetch registers as blocking (a
  // hard read behaves like a first load) rather than an invisible background revalidation.
  private hasData(key: string): boolean {
    const entry = this.entries.get(key)
    if (!entry || entry.invalidated === 'hard') return false
    return this.config.hasData(entry.data)
  }

  // Registers one background revalidation for a key, deduped through the in-flight map. The fetch
  // runs on a microtask so a read never writes tracked state during render.
  private scheduleRevalidate(key: string, dims: TDims): Promise<void> {
    const existing = this.inFlight.get(key)
    if (existing) return existing

    return this.track(key, Promise.resolve().then(() => this.performFetch(key, dims)), !this.hasData(key))
  }

  /**
   * Fresh entries trigger nothing; stale or missing entries schedule exactly one revalidation,
   * unless the key is backing off after a failure — then its own timer owns the next attempt.
   * Also one of the two points the eviction sweep runs from, so a read-heavy session keeps the map
   * bounded without waiting on a write.
   */
  ensure(requests: KeyRequest<TDims>[]): void {
    const now = Date.now()
    for (const { key, dims } of requests) {
      if (this.isStale(key, now) && !this.isBackingOff(key, now)) this.scheduleRevalidate(key, dims)
    }
    this.scheduleEviction()
  }

  /** The in-flight promises for these keys, for callers gating one keyed set on another. */
  pendingFor(requests: KeyRequest<TDims>[]): Promise<void>[] {
    return requests
      .map(({ key }) => this.inFlight.get(key))
      .filter((p): p is Promise<void> => !!p)
  }

  /**
   * Schedules `deferred` only once the `gatedOn` fetches have settled — so a low-priority or
   * speculative family never competes for a connection slot with the fetches the user is actually
   * waiting on. If `gatedOn` is empty, the deferred keys are ensured immediately. Attaching the
   * `.then` is render-safe (no tracked write); the deferred `ensure` runs in a later microtask, off
   * the render pass.
   *
   * Takes PROMISES rather than key requests so the gate can span caches: the availability engine
   * gates its times fetches on its dates fetches, and those live in two separate instances, so a
   * gate resolved against `this.inFlight` could never see them. Pair with `pendingFor` on whichever
   * cache owns the gating keys.
   */
  ensureDeferred(deferred: KeyRequest<TDims>[], gatedOn: Promise<void>[]): void {
    const wait = gatedOn.length ? Promise.all(gatedOn) : Promise.resolve()
    void wait.then(() => {
      if (!this.alive()) return
      this.ensure(deferred)
    })
  }

  /**
   * Unconditionally revalidates, even mid-flight: an in-flight fetch is superseded by chaining a
   * fresh fetch after it settles (the in-flight one is discarded by the current-fetch guard), so a
   * forced refresh never resolves with pre-invalidation data. Its caller awaits the returned
   * promise directly, so it does not additionally block settled().
   */
  forceRevalidate(key: string, dims: TDims): Promise<void> {
    this.clearBackoff(key)
    const existing = this.inFlight.get(key) ?? Promise.resolve()

    return this.track(key, existing.then(() => this.performFetch(key, dims)), false)
  }

  // -------------------------------------------------------------------------------------------
  // Invalidation
  // -------------------------------------------------------------------------------------------

  /**
   * Invalidates every entry `predicate(key, entry)` matches (default: all).
   *
   * **Soft** (default) zeroes `dataUpdatedAt` so the entry keeps serving its data and revalidates
   * on the next `ensure`. **Hard** additionally condemns the entry: it stops being usable, reads
   * of it behave like a first load, and a failed refetch never resurrects it — only a successful
   * response clears the flag.
   *
   * This only flags. Deciding which of the flagged keys deserve a force-refetch is scope policy,
   * so the caller follows up with `forceRevalidate` for the keys it still wants.
   */
  invalidate(options: InvalidateOptions<TData> = {}): void {
    const { mode = 'soft', predicate = () => true } = options

    for (const [key, entry] of this.entries) {
      if (!predicate(key, entry)) continue
      this.clearBackoff(key)
      this.entries.set(key, {
        ...entry,
        dataUpdatedAt: 0,
        ...(mode === 'hard' && { invalidated: 'hard' as const })
      })
    }
  }

  /** Marks every entry stale without fetching; the next `ensure` revalidates. */
  markAllStale(): void {
    this.invalidate()
  }

  // Returning to a backgrounded tab stales every entry so the next ensure refetches; entries stay
  // put until then. Torn down with the ctx.
  private setupVisibilityRevalidation(): void {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') this.markAllStale()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    registerDestructor(this.ctx, () => document.removeEventListener('visibilitychange', onVisibilityChange))
  }

  // -------------------------------------------------------------------------------------------
  // Eviction (gcTime + LRU cap)
  // -------------------------------------------------------------------------------------------

  // An entry's read baseline: its last read, or its write stamp if it was never read (so gcTime is
  // measured from when the entry entered the map, never from epoch zero).
  private readAt(key: string, entry: CacheEntry<TData>): number {
    return this.lastReadAt.get(key) ?? entry.dataUpdatedAt
  }

  private drop(key: string): void {
    this.entries.delete(key)
    this.lastReadAt.delete(key)
    this.inFlight.delete(key)
    this.clearBackoff(key)
  }

  /**
   * Reclaims entries so a long session's map stays bounded. Two independent passes:
   *
   * - **gcTime.** An entry unread for longer than gcTime is cold and is deleted.
   * - **LRU cap.** While the map exceeds maxEntries, the least-recently-read entry is dropped.
   *
   * Two kinds of entry are NEVER evicted by either pass:
   *
   * - **Pinned keys.** Whatever the caller still wants. Evicting one guarantees an immediate
   *   refetch, and with the map at the cap that becomes an evict -> refetch -> evict thrash.
   * - **In-flight keys.** Their fetch is about to write; dropping the in-flight promise would make
   *   the settled write discard itself (the current-fetch guard would no longer match).
   *
   * If only pinned / in-flight entries remain over the cap, nothing is evictable and the cap
   * yields to pinning.
   *
   * MUST run off the render pass — mutating the TrackedMap re-dirties derivations over it, which
   * is exactly how a projection recomputes after an eviction, but doing it mid-compute would loop.
   */
  runEviction(now: number = Date.now()): void {
    const pinned = this.config.pinnedKeys?.() ?? new Set<string>()

    for (const [key, entry] of this.entries) {
      if (this.inFlight.has(key) || pinned.has(key)) continue
      if (now - this.readAt(key, entry) > this.gcTime) this.drop(key)
    }

    while (this.entries.size > this.maxEntries) {
      let victimKey: string | undefined
      let victimReadAt = Infinity
      for (const [key, entry] of this.entries) {
        if (this.inFlight.has(key) || pinned.has(key)) continue
        const at = this.readAt(key, entry)
        if (at < victimReadAt) {
          victimReadAt = at
          victimKey = key
        }
      }
      // Only pinned / in-flight entries remain — nothing evictable, so the cap yields.
      if (!victimKey) break
      this.drop(victimKey)
    }
  }

  /** Runs `runEviction` on a deduped microtask, so a burst of reads and writes costs one pass. */
  scheduleEviction(): void {
    if (this.evictionScheduled) return
    this.evictionScheduled = true
    void Promise.resolve().then(() => {
      this.evictionScheduled = false
      this.runEviction()
    })
  }
}
